import { TASK_TYPES, TASK_TYPE_LABELS, type ColorToken } from '@hearth/shared';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { Checkbox, Field, Select, Switch } from '../../components/ui';
import { colorClasses } from '../../lib/colors';
import { TASK_TYPE_COLORS, TASK_TYPE_ICONS } from '../tasks/taskAppearance';
import { MiniMonth } from './MiniMonth';
import { type CalendarPreferences, MAX_CALENDAR_PEOPLE, type WeekStart, formatDateParam } from './model';

export type PersonOption = { id: string; label: string; color: ColorToken };

export type CalendarSidebarProps = {
  date: Date;
  /** Days the main view shows, shaded in the mini month (day and week views). */
  range: { start: Date; end: Date } | null;
  now: Date;
  people: PersonOption[];
  preferences: CalendarPreferences;
  onPreferencesChange: (patch: Partial<CalendarPreferences>) => void;
  onSelectDate: (day: Date) => void;
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h2 className="px-1 text-2xs font-semibold tracking-wide text-slate-500 uppercase">{title}</h2>
      {children}
    </section>
  );
}

/** Mini month, whose calendars and which entry types are shown, and how the grid looks. */
export function CalendarSidebar({
  date,
  range,
  now,
  people,
  preferences,
  onPreferencesChange,
  onSelectDate,
}: CalendarSidebarProps) {
  const togglePerson = (id: string, on: boolean) =>
    onPreferencesChange({
      people: on
        ? [...preferences.people, id].slice(0, MAX_CALENDAR_PEOPLE)
        : preferences.people.filter((p) => p !== id),
    });
  const toggleType = (type: (typeof TASK_TYPES)[number], on: boolean) =>
    onPreferencesChange({
      hiddenTypes: on
        ? preferences.hiddenTypes.filter((t) => t !== type)
        : [...preferences.hiddenTypes, type],
    });

  return (
    <div className="space-y-5">
      <MiniMonth
        key={formatDateParam(new Date(date.getFullYear(), date.getMonth(), 1))}
        date={date}
        range={range}
        weekStartsOn={preferences.weekStartsOn}
        now={now}
        onSelect={onSelectDate}
      />

      <Section title="People">
        <ul className="space-y-1">
          {people.map((person) => (
            <li key={person.id} className="px-1">
              <Checkbox
                checked={preferences.people.includes(person.id)}
                onChange={(event) => togglePerson(person.id, event.target.checked)}
                label={
                  <span className="flex items-center gap-1.5">
                    <span
                      aria-hidden
                      className={clsx('size-2.5 shrink-0 rounded-sm', colorClasses(person.color).dot)}
                    />
                    <span className="truncate">{person.label}</span>
                  </span>
                }
              />
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Entry types">
        <ul className="space-y-1">
          {TASK_TYPES.map((type) => {
            const Icon = TASK_TYPE_ICONS[type];
            return (
              <li key={type} className="px-1">
                <Checkbox
                  checked={!preferences.hiddenTypes.includes(type)}
                  onChange={(event) => toggleType(type, event.target.checked)}
                  label={
                    <span className="flex items-center gap-1.5">
                      <Icon
                        size={13}
                        aria-hidden
                        className={clsx('shrink-0', colorClasses(TASK_TYPE_COLORS[type]).text)}
                      />
                      {TASK_TYPE_LABELS[type]}
                    </span>
                  }
                />
              </li>
            );
          })}
        </ul>
        <Switch
          className="px-1 pt-1"
          size="sm"
          label="Show completed"
          checked={preferences.showCompleted}
          onChange={(showCompleted) => onPreferencesChange({ showCompleted })}
        />
      </Section>

      <Section title="View">
        <div className="space-y-3 px-1">
          <Field label="Color by">
            <Select
              size="sm"
              value={preferences.colorBy}
              onChange={(event) =>
                onPreferencesChange({ colorBy: event.target.value === 'person' ? 'person' : 'type' })
              }
              options={[
                { value: 'type', label: 'Entry type' },
                { value: 'person', label: 'Person' },
              ]}
            />
          </Field>
          <Field label="Week starts on">
            <Select
              size="sm"
              value={String(preferences.weekStartsOn)}
              onChange={(event) =>
                onPreferencesChange({ weekStartsOn: (event.target.value === '1' ? 1 : 0) as WeekStart })
              }
              options={[
                { value: '0', label: 'Sunday' },
                { value: '1', label: 'Monday' },
              ]}
            />
          </Field>
          <Switch
            size="sm"
            label="Show weekends"
            description="In the week view"
            checked={preferences.showWeekends}
            onChange={(showWeekends) => onPreferencesChange({ showWeekends })}
          />
        </div>
      </Section>
    </div>
  );
}
